import { useMemo, useRef, useState, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import ChipItem from '@/components/elements/ChipItem';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import { PlaceEmployee } from '@/types/state/shared';
import { _t } from '@/locale';
import { EmployeeReviewAvatar } from '@/features/reviews/EmployeeReviewAvatar';
import { ServicesState } from '@/reducers/servicesReducer';
import { ReviewsState } from '@/reducers/reviewsReducer';
import useService from '@/hooks/useService';
import { useListCardsV2 } from '@/hooks/useListCardsV2';
import { toast } from 'react-toastify';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import ReadMore from '@/components/modules/readmore/ReadMore';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants/reviewConstants';

const INITIAL_VISIBLE_EMPLOYEES = 6;

const ReviewServiceEmployee = ({
  employeeId,
  selected,
  onEmployeeClick,
  serviceId,
}: {
  employeeId: number;
  selected: boolean;
  onEmployeeClick?: (employeeId: number, serviceId: number) => void;
  serviceId: number;
}) => {
  const employee = useSelector(
    (state: { employees: { all: Record<string, PlaceEmployee> } }) => state.employees.all[employeeId],
  );

  if (!employee) {
    return null;
  }
  const name = employee.about?.name || '';

  return (
    <ChipItem
      textClassName="!text-m"
      label={name}
      selected={selected}
      withSelectedIcon={false}
      onClick={() => onEmployeeClick?.(employee.id, serviceId)}
      avatar={<EmployeeReviewAvatar employeeId={employee.id} size={'xs'} />}
    />
  );
};

export const ReviewsServiceDetails = ({
  serviceId,
  employeeId,
  onEmployeeClick,
  uid,
  hideStaff,
}: {
  serviceId: number;
  employeeId?: number;
  hideStaff?: boolean;
  onEmployeeClick?: (employeeId: number, serviceId: number) => void;
  uid: string;
}) => {
  const service = useSelector((state: { services: ServicesState }) => state.services.all[serviceId]);

  const { loadService } = useService();
  const isListCardsV2 = useListCardsV2();

  useEffect(() => {
    if (!service) {
      // 404s when the service no longer exists; the panel then stays empty.
      loadService(serviceId).catch(() => {
        toast(({ closeToast }) => (
          <Snackbar type="danger" label={_t('reviewsServiceDetailsLoadError')} onClose={closeToast} />
        ));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  const reviewDetails = useSelector((state: { reviews: ReviewsState }) => state.reviews.byUid[uid]?.details);
  const searchResult = useSelector(
    (state: { reviews: ReviewsState }) =>
      state.reviews.byUid[uid]?.searchResult || { loading: false, fetched: false, count: 0 },
  );

  // `state.reviews[uid].details` is one shared slot for the whole modal session, not
  // partitioned per service — so right after this view mounts (switching service, or
  // returning to one), it can still hold a previous fetch's data until the new one resolves.
  // We only trust it once THIS mount has actually observed a fetch start and finish — not
  // merely because the shared loading/fetched flags currently read that way, since they also
  // flip for an employeeId-only refetch we already have valid content for (and shouldn't
  // blank the panel over), or could still reflect an unrelated fetch that finished before
  // this component even existed.
  const hasSeenFetchStartRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  if (searchResult.loading) {
    hasSeenFetchStartRef.current = true;
  }
  if (hasSeenFetchStartRef.current && !searchResult.loading && searchResult.fetched) {
    hasLoadedOnceRef.current = true;
  }

  const isDataReady = hasLoadedOnceRef.current;
  const employeeStats = isDataReady ? reviewDetails?.employeeStats : undefined;

  // Each employee keeps the display position it was first assigned; only the very first
  // computation for this mount ranks by the currently selected employee — later re-selecting
  // a different employee (which only changes `employeeId`, not the underlying stats) must not
  // reshuffle the list.
  const employeeRankRef = useRef<Map<number, number>>(new Map());

  const arrayOfEmployees = useMemo(() => {
    if (!employeeStats) {
      return [];
    }

    const ids = Object.keys(employeeStats)
      .map(Number)
      .filter((id) => employeeStats[id]?.count);

    const rankMap = employeeRankRef.current;
    const newIds = ids.filter((id) => !rankMap.has(id)).sort((a, b) => a - b);

    if (rankMap.size === 0 && employeeId) {
      newIds.sort((a, b) => (a === employeeId ? -1 : b === employeeId ? 1 : a - b));
    }

    newIds.forEach((id) => rankMap.set(id, rankMap.size));

    return [...ids].sort((a, b) => rankMap.get(a) - rankMap.get(b));
  }, [employeeStats, employeeId]);

  const reviewsStats = isDataReady ? reviewDetails?.stats : undefined;

  const [expanded, setExpanded] = useState(false);

  const hasMoreEmployees = arrayOfEmployees && arrayOfEmployees.length > INITIAL_VISIBLE_EMPLOYEES;

  const visibleEmployeeIds = expanded ? arrayOfEmployees : arrayOfEmployees?.slice(0, INITIAL_VISIBLE_EMPLOYEES);

  if (!service) {
    return null;
  }

  const getChips = () => {
    if (hideStaff || !arrayOfEmployees || arrayOfEmployees.length < 2 || !onEmployeeClick) {
      return null;
    }

    return (
      <div className="gap-xs mb-4 flex flex-wrap items-center">
        <ChipItem
          label={_t('All')}
          selected={!employeeId}
          withSelectedIcon={false}
          textClassName={'!text-m'}
          onClick={() => onEmployeeClick(0, serviceId)}
        />
        {visibleEmployeeIds.map((_employeeId: number) => (
          <ReviewServiceEmployee
            key={_employeeId}
            employeeId={_employeeId}
            serviceId={serviceId}
            selected={employeeId === _employeeId}
            onEmployeeClick={onEmployeeClick}
          />
        ))}
        {hasMoreEmployees && (
          <button
            className={'text-fg_link text-m text-m whitespace-nowrap underline'}
            onClick={() => setExpanded(!expanded)}>
            {expanded ? _t('reviewsServiceDetailsShowFewerEmployees') : _t('reviewsServiceDetailsShowMoreEmployees')}
          </button>
        )}
      </div>
    );
  };

  const detalItems = [
    reviewsStats?.count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED ? (
      <RatingV2 averageRating={reviewsStats?.score} size={'md'} ratingCount={reviewsStats?.count ?? 0} />
    ) : null,
    service.durationLabel ? <span className={'text-fg_secondary text-m'}>{service.durationLabel}</span> : null,
    service.priceLabel ? <span className={'text-fg_secondary text-m'}>{service.priceLabel}</span> : null,
  ].filter(Boolean);

  return (
    <div>
      <h5 className={'text-fg_primary mb-2 text-lg font-bold'}>{service.name}</h5>
      <div className={'mb-4 flex items-center gap-3'}>
        {detalItems.map((item, index) => {
          if (index === 0) {
            return <Fragment key={index}>{item}</Fragment>;
          }
          return (
            <Fragment key={index}>
              <span aria-hidden="true" className="bg-fg_secondary h-3xs w-3xs shrink-0 rounded-full" />
              {item}
            </Fragment>
          );
        })}
      </div>
      {isListCardsV2 ? (
        <ReadMore
          className={'mb-4 break-words'}
          textClassName={'text-m text-fg_secondary'}
          text={service?.about?.description || _t('serviceFallbackDescription')}
          maxCharacters={200}
        />
      ) : (
        <p className={'text-fg_secondary text-m mb-4 break-words'}>
          {service?.about?.description || _t('serviceFallbackDescription')}
        </p>
      )}
      {getChips()}
    </div>
  );
};
